import { useRef, useState, useCallback, useEffect } from 'react';

function useMediaPlayer(errorHandler) {
    const playerElementRef = useRef(null);
    const playerInstanceRef = useRef(null);
    const [status, setStatus] = useState('initializing');
    const [urlLoaded, setUrlLoaded] = useState();
    const handlePlayerInstanceError = useCallback(() => {
        const error = new Error('no player instance');
        if (errorHandler)
            errorHandler(error);
        else
            throw error;
    }, [errorHandler]);
    const handleStop = useCallback(() => {
        setStatus('stopped');
        setUrlLoaded(undefined);
    }, []);
    const initialize = useCallback((urls, type) => {
        const { current: divElement } = playerElementRef;
        if (!divElement)
            return;
        const playlist = urls.filter(x => x).map(url => ({ file: url, type }));
        if (playlist.length) {
            if (!playerInstanceRef.current) {
                const player = window.jwplayer(divElement).setup({
                    playlist: playlist,
                    autostart: false,
                    mute: false,
                });
                player.on('ready', () => setStatus('idle'));
                player.on('play', () => setStatus('playing'));
                player.on('playlistItem', args => setUrlLoaded(args.item.file));
                player.on('pause', () => setStatus('paused'));
                player.on('playlistComplete', handleStop);
                player.on('error', err => errorHandler(err));
                player.on('setupError', err => errorHandler(err));
                playerInstanceRef.current = player;
            }
            else {
                playerInstanceRef.current.load(playlist);
            }
        }
    }, [errorHandler, handleStop]);
    useEffect(() => {
        return () => {
            const { current: player } = playerInstanceRef;
            if (player)
                player.remove();
        };
    }, []);
    const play = useCallback(() => {
        const { current: playerInstance } = playerInstanceRef;
        if (!playerInstance) {
            handlePlayerInstanceError();
            return;
        }
        if (status === 'paused')
            playerInstance.play();
        else
            playerInstance.playlistItem(0);
    }, [status, handlePlayerInstanceError]);
    const pause = useCallback(() => {
        const { current: playerInstance } = playerInstanceRef;
        if (!playerInstance) {
            handlePlayerInstanceError();
            return;
        }
        playerInstance.pause();
    }, [handlePlayerInstanceError]);
    const stop = useCallback(() => {
        const { current: playerInstance } = playerInstanceRef;
        if (!playerInstance) {
            handlePlayerInstanceError();
            return;
        }
        playerInstance.stop();
        handleStop();
    }, [handlePlayerInstanceError, handleStop]);
    const mute = useCallback(() => {
        const { current: playerInstance } = playerInstanceRef;
        if (!playerInstance) {
            handlePlayerInstanceError();
            return;
        }
        playerInstance.setMute(true);
    }, [handlePlayerInstanceError]);
    const unmute = useCallback(() => {
        const { current: playerInstance } = playerInstanceRef;
        if (!playerInstance) {
            handlePlayerInstanceError();
            return;
        }
        if (playerInstance.getMute())
            playerInstance.setMute(false);
    }, [handlePlayerInstanceError]);
    return [
        playerElementRef,
        {
            initialize,
            mute,
            pause,
            play,
            status,
            stop,
            unmute,
            urlLoaded,
        },
    ];
}

export { useMediaPlayer as default };
